import { BoxProps } from "@mui/material";
import {
  animate,
  motion,
  useMotionTemplate,
  useMotionValue,
  useTransform,
} from "framer-motion";
import { useEffect, useRef } from "react";
import styled from "styled-components";

interface TiltCardProps extends BoxProps {
  containerRef: React.RefObject<HTMLElement>;
  canTilt?: boolean;
}

const TiltCard = ({
  containerRef,
  children,
  canTilt = true,
}: TiltCardProps) => {
  // mouse position
  const mouseX = useMotionValue(
    typeof window !== "undefined"
      ? containerRef?.current?.clientWidth ?? 0 / 2
      : 0
  );
  const mouseY = useMotionValue(
    typeof window !== "undefined"
      ? containerRef?.current?.clientHeight ?? 0 / 2
      : 0
  );

  // a reference for our animated card element
  const cardRef = useRef<HTMLDivElement>(null);

  // rotation
  const dampen = 1000;
  const rotateX = useTransform<number, number>(mouseY, (newMouseY) => {
    if (!cardRef.current) return 0;
    const rect = cardRef.current.getBoundingClientRect();
    const newRotateX = newMouseY - rect.top - rect.height / 2;
    return -newRotateX / dampen;
  });
  const rotateY = useTransform(mouseX, (newMouseX) => {
    if (!cardRef.current) return 0;
    const rect = cardRef.current.getBoundingClientRect();
    const newRotateY = newMouseX - rect.left - rect.width / 2;
    return newRotateY / dampen;
  });

  // sheen
  const diagonalMovement = useTransform<number, number>(
    [rotateX, rotateY],
    ([newRotateX, newRotateY]) => {
      const position: number = newRotateX + newRotateY;
      return position;
    }
  );
  const sheenPosition = useTransform(diagonalMovement, [-5, 5], [-100, 200]);
  const sheenOpacity = useTransform(
    sheenPosition,
    [-250, 50, 250],
    [0, 0.5, 0]
  );
  const sheenGradient = useMotionTemplate`linear-gradient(
    55deg,
    transparent,
    rgba(255 255 255 / ${sheenOpacity}) ${sheenPosition}%,
    transparent)`;

  // handle mouse move on document
  useEffect(() => {
    let localRef: HTMLElement;
    if (canTilt && containerRef?.current != null) {
      localRef = containerRef.current;

      const handleMouseMove = (e: MouseEvent) => {
        // animate mouse x and y
        animate(mouseX, e.clientX);
        animate(mouseY, e.clientY);
      };
      if (typeof window === "undefined") return;
      // recalculate grid on resize
      localRef.addEventListener("mousemove", handleMouseMove);
      // cleanup
      return () => {
        localRef?.removeEventListener("mousemove", handleMouseMove);
      };
    }
  }, [canTilt, containerRef, mouseX, mouseY]);

  return (
    <Container>
      <RotationWrapper
        style={{
          boxShadow: "0px 3px 24px rgba(0,0,0,0.5)",
          overflow: "hidden",
          borderRadius: 10,
          rotateX,
          rotateY,
        }}
      >
        <motion.div
          style={{ rotateX, rotateY, overflow: "hidden", borderRadius: 10 }}
          ref={canTilt ? cardRef : null}
        >
          {children}

          {canTilt && (
            <Sheen
              style={{
                backgroundImage: sheenGradient,
              }}
            />
          )}
        </motion.div>
      </RotationWrapper>
    </Container>
  );
};

export default TiltCard;

const RotationWrapper = styled(motion.div)`
  transform-style: preserve-3d;
  overflow: hidden;
`;

const Container = styled.div`
  perspective: 1000px;
  position: relative;
`;

const Sheen = styled(motion.div)`
  mix-blend-mode: overlay;
  pointer-events: none;
  position: absolute;
  height: 100%;
  width: 100%;
  z-index: 10;
  left: 0;
  top: 0;
`;
